import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../services/authProvider'
import store from '../../store/configureStore'
import { fetchReport, executeRecon, fetchCompanyLabels, fetchUnmappedOrgs } from '../actions/threatlockerReconActions'
import LoadingSpinner from '../Shared/LoadingSpinner'
import PageContainer from '../Shared/LayoutComponents/PageContainer'
import PageHeading from '../Shared/LayoutComponents/PageHeading'
import { Container, Row, Col, Card, CardBody, Badge } from 'reactstrap'
import ThreatlockerReconTable from './ThreatlockerReconTable'
import UnmappedOrgsModal from './UnmappedOrgsModal'

const ThreatlockerReconInner = () => {
  const dispatch = useDispatch()
  const [unmappedModalOpen, setUnmappedModalOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchReport())
    dispatch(fetchCompanyLabels())
    dispatch(fetchUnmappedOrgs())
  }, [dispatch])

  const { loading, report, executing, lastPushDate, unmappedOrgs } = useSelector((state) => state.threatlockerRecon)

  const formattedLastPush = React.useMemo(() => {
    if (!lastPushDate) return null
    return new Date(lastPushDate).toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }, [lastPushDate])

  // Filter out unmapped rows — dashboard only shows CW companies
  const dashboardRows = React.useMemo(() => {
    if (!report) return []
    return report.filter((r) => r.companyId != null)
  }, [report])

  const statusCounts = React.useMemo(() => {
    const counts = { total: 0, completed: 0, pending: 0, flagged: 0, blocked: 0, noChanges: 0, approved: 0, awaitingSync: 0 }
    dashboardRows.forEach((r) => {
      counts.total++
      switch (r.status) {
        case 'Completed': counts.completed++; break
        case 'Pending': counts.pending++; break
        case 'Flagged': counts.flagged++; break
        case 'Blocked': counts.blocked++; break
        case 'NoChanges': counts.noChanges++; break
        case 'Approved': counts.approved++; break
        case 'AwaitingSync': counts.awaitingSync++; break
        default: break
      }
    })
    return counts
  }, [dashboardRows])

  const unmappedCount = (unmappedOrgs || []).length

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-center mb-4">
          <Col>
            <PageHeading>Threatlocker Reconciliation</PageHeading>
            {formattedLastPush && (
              <small className="text-muted">Data last refreshed on {formattedLastPush}</small>
            )}
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            {unmappedCount > 0 && (
              <button
                className="btn btn-outline-warning d-flex align-items-center gap-2"
                onClick={() => setUnmappedModalOpen(true)}
              >
                Unmapped <Badge color="warning">{unmappedCount}</Badge>
              </button>
            )}
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              disabled={executing || loading}
              onClick={() => dispatch(executeRecon())}
            >
              {executing ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" />
                  Executing...
                </>
              ) : (
                'Execute Auto-Recon'
              )}
            </button>
          </Col>
        </Row>

        {/* Summary cards */}
        <Row className="mb-4">
          <Col md={2}>
            <Card>
              <CardBody className="text-center">
                <h4 className="mb-0">{statusCounts.total}</h4>
                <small className="text-muted">Total</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-success">
              <CardBody className="text-center">
                <h4 className="mb-0 text-success">{statusCounts.completed}</h4>
                <small className="text-muted">Completed</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-primary">
              <CardBody className="text-center">
                <h4 className="mb-0 text-primary">{statusCounts.pending + statusCounts.noChanges + statusCounts.approved}</h4>
                <small className="text-muted">Ready</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-warning">
              <CardBody className="text-center">
                <h4 className="mb-0 text-warning">{statusCounts.flagged}</h4>
                <small className="text-muted">Flagged</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-danger">
              <CardBody className="text-center">
                <h4 className="mb-0 text-danger">{statusCounts.blocked}</h4>
                <small className="text-muted">Blocked</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card style={{ borderColor: '#adb5bd' }}>
              <CardBody className="text-center">
                <h4 className="mb-0" style={{ color: '#adb5bd' }}>{statusCounts.awaitingSync}</h4>
                <small className="text-muted">Awaiting Sync</small>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <ThreatlockerReconTable rows={dashboardRows} />
        )}
      </Container>

      <UnmappedOrgsModal
        isOpen={unmappedModalOpen}
        toggle={() => setUnmappedModalOpen(false)}
      />
    </PageContainer>
  )
}

const ThreatlockerRecon = () => {
  return (
    <AzureAD provider={authProvider} reduxStore={store}>
      {({ accountInfo }) => {
        const roles = accountInfo?.account?.idToken?.roles || []
        const isAdminOrSales = roles.includes('Admin') || roles.includes('Sales')

        if (!isAdminOrSales) {
          return (
            <PageContainer>
              <Container fluid className="mt-3">
                <p>You do not have permission to view this page.</p>
              </Container>
            </PageContainer>
          )
        }

        return <ThreatlockerReconInner />
      }}
    </AzureAD>
  )
}

export default ThreatlockerRecon
